import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Wrench, Mail, Image } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import MediaFrame from "../components/MediaFrame";
import Reveal from "../components/Reveal";

const CONTACTS = [
  ["Booking", "booking@joymessenger.fr"],
  ["Presse", "presse@joymessenger.fr"],
  ["Production", "production@joymessenger.fr"],
];

export default function Press() {
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="press-page" className="pt-28 sm:pt-36">
      <div className="jm-container pb-24 sm:pb-32">
        <Reveal>
          <p className="jm-overline mb-5">Espace professionnel</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-jm-bone leading-[1.08] max-w-3xl">
            Presse, booking & production
          </h1>
          <p className="mt-6 max-w-2xl text-jm-smoke leading-relaxed">
            Programmateurs, journalistes, labels : biographies, photos HD, dossiers de presse, fiches techniques,
            vidéos et liens d'écoute — tout est accessible ici, sans inscription.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="press-contacts">
            {CONTACTS.map(([label, email]) => (
              <a
                key={label}
                href={`mailto:${email}`}
                data-testid={`press-contact-${label.toLowerCase()}`}
                className="group flex items-center justify-between border border-jm-brass/20 bg-jm-card/60 px-6 py-5 hover:border-jm-amber transition-colors duration-300"
              >
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-jm-mute">{label}</span>
                  <span className="block mt-1.5 text-sm text-jm-bone group-hover:text-jm-amber transition-colors">{email}</span>
                </span>
                <Mail size={16} className="text-jm-mute group-hover:text-jm-amber transition-colors" />
              </a>
            ))}
          </div>
        </Reveal>

        <div className="mt-20 space-y-6" data-testid="press-artists">
          {artists.map((a, i) => (
            <Reveal key={a.id} delay={0.05 * i}>
              <article
                className="grid grid-cols-1 lg:grid-cols-12 border border-jm-brass/15 bg-jm-card/50 overflow-hidden"
                data-testid={`press-artist-${a.id}`}
              >
                <MediaFrame
                  label={a.name}
                  sublabel="Photo HD — sur demande"
                  accent={a.accent}
                  className="lg:col-span-4 aspect-[16/10] lg:aspect-auto lg:min-h-[280px]"
                />
                <div className="lg:col-span-8 p-7 sm:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: a.accent }}>
                    {a.genre}
                  </p>
                  <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-jm-bone">{a.name}</h2>
                  <p className="mt-3 font-serif italic text-lg text-jm-bone/70">{a.phrase}</p>
                  <p className="mt-4 text-sm text-jm-smoke leading-relaxed max-w-2xl">{a.bio[0]}</p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <a
                      href={`${BACKEND_URL}/api/artists/${a.id}/press-kit`}
                      data-testid={`press-kit-${a.id}`}
                      className="inline-flex items-center gap-2 border border-jm-brass/30 px-4 py-2.5 text-[13px] text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors"
                    >
                      <FileText size={14} /> Dossier de presse <Download size={13} />
                    </a>
                    <a
                      href={`${BACKEND_URL}/api/artists/${a.id}/tech-rider`}
                      data-testid={`tech-rider-${a.id}`}
                      className="inline-flex items-center gap-2 border border-jm-brass/30 px-4 py-2.5 text-[13px] text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors"
                    >
                      <Wrench size={14} /> Fiche technique <Download size={13} />
                    </a>
                    <span className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] text-jm-mute border border-transparent">
                      <Image size={14} /> Photos HD : {a.press_email}
                    </span>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-14 text-sm text-jm-mute">
            Une question, un dossier complet, une date à proposer ?{" "}
            <Link to="/contact" className="text-jm-brass hover:text-jm-amber transition-colors">
              Écrivez-nous
            </Link>
            .
          </p>
        </Reveal>
      </div>
    </main>
  );
}
